import { useMemo } from 'react';
import { Paper, Box } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import { useSelector } from 'react-redux';
import {
  formatRupeesIndianCompact,
  formatIndianAuto,
  premiumTooltip,
  horizontalGrid,
  indianValueAxis,
  indianCategoryAxis,
  defaultAnimation,
} from './echartsConfig';
import { reportChartCanvasSx, reportChartPaperSx } from './reportChartLayout';
import ReportCardHeader, { ReportInsightBanner } from './ReportCardHeader';
import ReportKpiStrip, { kpiMapToItems } from './ReportKpiStrip';
import { getCurrencySymbol } from 'utils/hooks/getCurrency';
import { getCurrency as currencySelector } from 'utils/redux/reducer/authentication-slice';

const norm = s => String(s || '').toLowerCase();
const toNum = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const sumArr = arr => (arr || []).reduce((s, v) => s + toNum(v), 0);

const ASSIGNED_GRAY = '#78909C';
const CONFIRMED_GREEN = '#66BB6A';
const TENTATIVE_AMBER = '#FFC107';
const ACTUALIZED_PURPLE = '#9575CD';

function findDsIdx(datasets, needle) {
  return datasets.findIndex(d => norm(String(d.label)).includes(needle));
}

function extractSourceData(chartData) {
  const labels = chartData?.labels || [];
  const ds = chartData?.datasets || [];

  const iAssign = findDsIdx(ds, 'assign');
  const iConf = findDsIdx(ds, 'confirm');
  const iAct = findDsIdx(ds, 'actual');
  const iTent = findDsIdx(ds, 'tent');

  const assigned = labels.map((_, i) => (iAssign >= 0 ? toNum(ds[iAssign].data?.[i]) : 0));
  const confirmed = labels.map((_, i) => (iConf >= 0 ? toNum(ds[iConf].data?.[i]) : 0));
  const actualized = labels.map((_, i) => (iAct >= 0 ? toNum(ds[iAct].data?.[i]) : 0));
  const tentative = labels.map((_, i) => (iTent >= 0 ? toNum(ds[iTent].data?.[i]) : 0));

  const visible = labels.map((_, i) => confirmed[i] + tentative[i] + actualized[i]);
  const keep = visible.map(v => v > 0);
  const pick = arr => (arr || []).filter((_, i) => keep[i]);

  return {
    labels: labels.filter((_, i) => keep[i]).map(String),
    assigned: pick(assigned),
    confirmed: pick(confirmed),
    tentative: pick(tentative),
    actualized: pick(actualized),
  };
}

function portfolioStackTooltip(assignedArr) {
  return params => {
    if (!Array.isArray(params) || !params.length) return '';
    const idx = params[0].dataIndex;
    const title = `<div style="font-weight:600;font-size:13px;color:#f8fafc;margin-bottom:6px">${params[0].axisValueLabel}</div>`;

    const row = (label, value, color) => {
      if (!value) return '';
      const dot = `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color};margin-right:6px"></span>`;
      return `<div style="display:flex;justify-content:space-between;align-items:center;gap:12px">${dot}<span style="flex:1">${label}</span><span style="font-weight:600;font-variant-numeric:tabular-nums">${getCurrencySymbol()}${formatIndianAuto(value)}</span></div>`;
    };

    let confVal = 0;
    let tentVal = 0;
    let actVal = 0;
    params.forEach(p => {
      const L = norm(p.seriesName);
      if (L.includes('actual')) actVal = toNum(p.value);
      else if (L.includes('confirm')) confVal = toNum(p.value);
      else if (L.includes('tent')) tentVal = toNum(p.value);
    });
    const assignedVal = assignedArr[idx] || 0;
    const pipeline = confVal + tentVal;

    let html = title;
    html += row('Assigned', assignedVal, ASSIGNED_GRAY);
    html += `<div style="border-top:1px solid rgba(148,163,184,0.15);margin:4px 0"></div>`;
    html += row('Confirmed', confVal, CONFIRMED_GREEN);
    html += row('Tentative', tentVal, TENTATIVE_AMBER);
    html += row('Actualized', actVal, ACTUALIZED_PURPLE);
    if (pipeline > 0 && assignedVal > 0) {
      const pipelinePct = Math.round((pipeline / assignedVal) * 1000) / 10;
      html += `<div style="color:#94a3b8;font-size:11px;margin:4px 0 0 2px">Pipeline vs assigned: ${formatRupeesIndianCompact(pipeline)} (${pipelinePct}%)</div>`;
    }
    return html;
  };
}

function buildHorizontalStackedOption(extracted) {
  const { labels, assigned, confirmed, tentative, actualized } = extracted;
  if (!labels.length) return {};

  return {
    ...defaultAnimation,
    tooltip: {
      ...premiumTooltip,
      formatter: portfolioStackTooltip(assigned),
    },
    legend: { show: false },
    grid: horizontalGrid,
    xAxis: {
      ...indianValueAxis,
    },
    yAxis: {
      ...indianCategoryAxis,
      data: labels,
      axisLabel: {
        ...indianCategoryAxis.axisLabel,
        width: 120,
        overflow: 'truncate',
      },
    },
    series: [
      {
        name: 'Confirmed',
        type: 'bar',
        stack: 'portfolio',
        data: confirmed,
        barWidth: '55%',
        itemStyle: { color: CONFIRMED_GREEN, borderRadius: 0 },
        emphasis: { itemStyle: { opacity: 0.88 } },
      },
      {
        name: 'Tentative',
        type: 'bar',
        stack: 'portfolio',
        data: tentative,
        barWidth: '55%',
        itemStyle: { color: TENTATIVE_AMBER, borderRadius: 0 },
        emphasis: { itemStyle: { opacity: 0.88 } },
      },
      {
        name: 'Actualized',
        type: 'bar',
        stack: 'portfolio',
        data: actualized,
        barWidth: '55%',
        itemStyle: { color: ACTUALIZED_PURPLE, borderRadius: 0 },
        emphasis: { itemStyle: { opacity: 0.88 } },
      },
    ],
  };
}

const SourcePortfolioHorizontalStacked = ({
  data,
  handleExport,
  handleEnlarge,
  showButton = true,
  embeddedInDialog = false,
}) => {
  const currencyContext = useSelector(currencySelector);
  const currencyKey = `${currencyContext?.currencySymbol || ''}|${currencyContext?.currency || ''}|${currencyContext?.currencyLocale || ''}`;
  const extracted = useMemo(() => extractSourceData(data), [data]);
  const option = useMemo(() => buildHorizontalStackedOption(extracted), [extracted]);

  const displayName = data?.name || 'Source-wise revenue';
  const apiKpis = kpiMapToItems(data?.kpiMap);
  const infoTooltip =
    data?.infoText ||
    'Portfolio mix by source — horizontal stacked contribution (confirmed, tentative, actualized). Assigned shown in tooltip.';

  const totalAssigned = sumArr(extracted.assigned);
  const totalConfirmed = sumArr(extracted.confirmed);
  const totalTentative = sumArr(extracted.tentative);
  const totalPipeline = totalConfirmed + totalTentative;
  const totalActualized = sumArr(extracted.actualized);
  const pipelinePct = totalAssigned > 0 ? Math.round((totalPipeline / totalAssigned) * 1000) / 10 : 0;
  const actPct = totalAssigned > 0 ? Math.round((totalActualized / totalAssigned) * 1000) / 10 : 0;

  if (!extracted.labels.length) {
    return null;
  }

  const paperSx = embeddedInDialog
    ? {
        ...reportChartPaperSx,
        flex: 1,
        minHeight: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: 'none',
        border: 'none',
        px: 0,
        py: 0,
      }
    : reportChartPaperSx;

  const canvasSx = embeddedInDialog
    ? {
        flex: '1 1 auto',
        minHeight: 360,
        height: '100%',
        minWidth: 0,
        position: 'relative',
      }
    : reportChartCanvasSx;

  return (
    <Paper sx={paperSx}>
      {!embeddedInDialog && (
        <ReportCardHeader
          title={displayName}
          insight={infoTooltip}
          handleExport={handleExport}
          handleEnlarge={handleEnlarge}
          showButton={showButton}
        />
      )}

      <ReportKpiStrip
        items={
          apiKpis || [
            { label: 'Total Assigned', value: formatRupeesIndianCompact(totalAssigned) },
            { label: 'Pipeline', value: `${formatRupeesIndianCompact(totalPipeline)} (${pipelinePct}%)` },
            { label: 'Actualized', value: `${formatRupeesIndianCompact(totalActualized)} (${actPct}%)` },
          ]
        }
      />

      <ReportInsightBanner text={data?.insightText} style={data?.insightStyle} />

      <Box sx={canvasSx}>
        <ReactECharts key={currencyKey} option={option} style={{ width: '100%', height: '100%' }} notMerge />
      </Box>
    </Paper>
  );
};

export default SourcePortfolioHorizontalStacked;
